import React, { useState, useMemo } from 'react';
import { Card, Table, Row, Col, OverlayTrigger, Tooltip } from 'react-bootstrap';
import Header from '~/Components/Header/Header';
import { Formik, Form as FormikForm, Field, ErrorMessage } from 'formik';
import { FaAngleUp, FaAngleDown } from 'react-icons/fa';
import * as Yup from 'yup';
import { toast } from 'react-toastify';
import {buscarAlcadaAtiva, salvarAlcada} from '~/Services/Service';

const TelaAlcadas = ({ empreendimentoId, alcadasData, alcadasDataLista, onRefresh }) => {
  const [removing, setRemoving] = useState(false);
  const [ordenacao, setOrdenacao] = useState({ coluna: null, direcao: 'asc' });
  const [colunaHover, setColunaHover] = useState(null);

  const handleOrdenacao = coluna => {
    setOrdenacao(prev => ({
      coluna,
      direcao:
        prev.coluna === coluna && prev.direcao === 'asc' ? 'desc' : 'asc',
    }));
  };

  const getIconeOrdenacao = coluna => {
    if (ordenacao.coluna !== coluna) return '';
    return ordenacao.direcao === 'asc' ? <FaAngleUp /> : <FaAngleDown />;
  };

  const dadosOrdenados = useMemo(() => {
    if (!alcadasDataLista || !ordenacao.coluna) return alcadasDataLista || [];

    return [...alcadasDataLista].sort((a, b) => {
      const valorA = a[ordenacao.coluna];
      const valorB = b[ordenacao.coluna];

      if (valorA == null && valorB == null) return 0;
      if (valorA == null) return ordenacao.direcao === 'asc' ? 1 : -1;
      if (valorB == null) return ordenacao.direcao === 'asc' ? -1 : 1;

      if (typeof valorA === 'string') {
        const comparacao = valorA.localeCompare(valorB, 'pt-BR');
        return ordenacao.direcao === 'asc' ? comparacao : -comparacao;
      }

      if (ordenacao.direcao === 'asc') {
        return valorA - valorB;
      }
      return valorB - valorA;
    });
  }, [alcadasDataLista, ordenacao]);

  const tableStyles = {
    wrapper: {
      borderRadius: '8px',
      border: '1px solid #dee2e6',
      maxHeight: '300px',
      overflowY: 'auto',
    },
    header: {
      backgroundColor: '#0747a6',
      color: '#fff',
      borderColor: '#0747a6',
      cursor: 'pointer',
      userSelect: 'none',
      transition: 'background-color 0.2s',
      position: 'sticky',
      top: 0,
      zIndex: 1,
    },
    headerHover: {
      backgroundColor: '#053580',
    },
  };

  const colunasAlcadas = [
    { chave: 'nome_usuario', label: 'Usuário' },
    { chave: 'setor', label: 'Setor' },
    { chave: 'tipo', label: 'Tipo' },
    { chave: 'alteracao', label: 'Alteração', sortable: false },
    { chave: 'data_inclusao', label: 'Data' },
  ];

  const getInitialValues = () => {
    if (alcadasData) {
      return {
        corretorDesconto: alcadasData.percentual_corretor != null ? alcadasData.percentual_corretor : 0,
        corretorCarencia: alcadasData.meses_corretor != null ? alcadasData.meses_corretor : 0,
        gerenteDesconto: alcadasData.percentual_gerente != null ? alcadasData.percentual_gerente : 0,
        gerenteCarencia: alcadasData.meses_gerente != null ? alcadasData.meses_gerente : 0,
      };
    }
    return {
      corretorDesconto: 0,
      corretorCarencia: 0,
      gerenteDesconto: 0,
      gerenteCarencia: 0,
    };
  };

const validationSchema = Yup.object().shape({
  corretorDesconto: Yup.number()
    .typeError('Deve ser um número')
    .min(0.01, 'Deve ser maior que 0')
    .required('Obrigatório'),
  corretorCarencia: Yup.number()
    .typeError('Deve ser um número')
    .min(0, 'Min 0')
    .integer('Inteiro')
    .required('Obrigatório'),
  gerenteDesconto: Yup.number()
    .typeError('Deve ser um número')
    .min(0.01, 'Deve ser maior que 0')
    .test('maior-que-corretor', 'Deve ser maior que o desconto do corretor', function(value) {
      const { corretorDesconto } = this.parent;
      return value > corretorDesconto;
    })
    .required('Obrigatório'),
  gerenteCarencia: Yup.number()
    .typeError('Deve ser um número')
    .min(0, 'Min 0')
    .integer('Inteiro')
    .required('Obrigatório'),
});

const handleSubmit = async (values, { setSubmitting }) => {
  try {
    const payload = {
      familia: empreendimentoId,
      corretor_desconto: values.corretorDesconto,
      corretor_carencia: values.corretorCarencia,
      gerente_desconto: values.gerenteDesconto,
      gerente_carencia: values.gerenteCarencia,
    };
    await salvarAlcada(payload);
    toast.success('Alçada salva com sucesso!');
    if (onRefresh) {
      await onRefresh();
    }
  } catch (error) {
    console.error('Erro ao salvar alçada:', error);
    toast.error((error && error.message) || 'Erro ao salvar alçada.');
  } finally {
    setSubmitting(false);
  }
};

  const handleSubmitApagar = async ( ) => {
    if (!alcadasData || 
      (alcadasData.percentual_corretor === 0 && 
       alcadasData.percentual_gerente === 0)) {
    toast.warning('Não há alçada cadastrada para remover.');
    return;
  }
    setRemoving(true);
    try {
      const payload = {
        familia: empreendimentoId,
        corretor_desconto: 0,
        corretor_carencia: 0,
        gerente_desconto: 0,
        gerente_carencia:0
      };
      await salvarAlcada(payload)
      toast.success('Alçada removida com sucesso!');
      if (onRefresh) {
        await onRefresh();
      }
    } catch (error) {
      console.error('Erro ao remover alçada:', error);
      toast.error((error && error.message) || 'Erro ao remover alçada.');
    } finally {
      setRemoving(false);
    }
  };

  const formatarData = (dataString) => {
    if (!dataString) return '-';
    try {
      const data = new Date(dataString);
      return data.toLocaleString('pt-BR');
    } catch (e) {
      return dataString;
    }
  };

  const formatarPercentual = (valor) => {
      const num = Number(valor);
      const formatted = num % 1 === 0 ? num.toFixed(0) : num.toFixed(1);
      return formatted.replace('.', ',') + '%';
  };

  const getValorOuTraco = (valor) => {
    return valor != null ? valor : '-';
  };

  return (
    <div className="bg-white my-3">
      <Header title="Alçadas do empreendimento" />
      <Row className="px-3 my-3">
        {/* Coluna da Esquerda - Cadastro */}
        <Col xs={12} lg={6} className="mb-3 mb-lg-0">
          <Formik
            initialValues={getInitialValues()}
            validationSchema={validationSchema}
            onSubmit={handleSubmit}
            enableReinitialize
          >
            {({ isSubmitting, resetForm }) => (
              <FormikForm>
                <Card className="h-100">
                  <Card.Header className="bg-light">
                    <h5 className="mb-0">Cadastro</h5>
                  </Card.Header>
                  <Card.Body>
                    {/* Dados Corretores */}
                    <h6 className="text-center mb-3">Dados Corretores</h6>
                    <Row className="mb-4">
                      <Col xs={6}>
                        <div className="form-group mb-0">
                          <OverlayTrigger
                            placement="top"
                            overlay={<Tooltip>Desconto em porcentagem (%)</Tooltip>}
                          >
                            <label className="small mb-1" style={{ cursor: 'help' }}>Desconto - %</label>
                          </OverlayTrigger>
                          <Field
                            name="corretorDesconto"
                            type="number"
                            min={0}
                            step="0.01"
                            className="form-control form-control-sm"
                            disabled={isSubmitting || removing}
                          />
                          <ErrorMessage
                            name="corretorDesconto"
                            component="div"
                            className="text-danger small"
                          />
                        </div>
                      </Col>
                      <Col xs={6}>
                        <div className="form-group mb-0">
                          <OverlayTrigger
                            placement="top"
                            overlay={<Tooltip>Quantidade de meses</Tooltip>}
                          >
                            <label className="small mb-1" style={{ cursor: 'help' }}>Carencia - Meses</label>
                          </OverlayTrigger>
                          <Field
                            name="corretorCarencia"
                            type="number"
                            min={0}
                            className="form-control form-control-sm"
                            disabled={isSubmitting || removing}
                          />
                          <ErrorMessage
                            name="corretorCarencia"
                            component="div"
                            className="text-danger small"
                          />
                        </div>
                      </Col>
                    </Row>

                    {/* Dados Gerentes */}
                    <h6 className="text-center mb-3">Dados Gerentes de Imobiliária</h6>
                    <Row className="mb-4">
                      <Col xs={6}>
                        <div className="form-group mb-0">
                          <OverlayTrigger
                            placement="top"
                            overlay={<Tooltip>Desconto em porcentagem (%)</Tooltip>}
                          >
                            <label className="small mb-1" style={{ cursor: 'help' }}>Desconto - %</label>
                          </OverlayTrigger>
                          <Field
                            name="gerenteDesconto"
                            type="number"
                            min={0}
                            step="0.01"
                            className="form-control form-control-sm"
                            disabled={isSubmitting || removing}
                          />
                          <ErrorMessage
                            name="gerenteDesconto"
                            component="div"
                            className="text-danger small"
                          />
                        </div>
                      </Col>
                      <Col xs={6}>
                        <div className="form-group mb-0">
                          <OverlayTrigger
                            placement="top"
                            overlay={<Tooltip>Quantidade de meses</Tooltip>}
                          >
                            <label className="small mb-1" style={{ cursor: 'help' }}>Carencia - Meses</label>
                          </OverlayTrigger>
                          <Field
                            name="gerenteCarencia"
                            type="number"
                            min={0}
                            className="form-control form-control-sm"
                            disabled={isSubmitting || removing}
                          />
                          <ErrorMessage
                            name="gerenteCarencia"
                            component="div"
                            className="text-danger small"
                          />
                        </div>
                      </Col>
                    </Row>

                    {/* Botões - empilham em mobile */}
                    <Row>
                      <Col xs={12} sm={4} className="mb-2 mb-sm-0">
                        <button
                          type="submit"
                          className="btn btn-success btn-sm w-100"
                          disabled={isSubmitting || removing}
                        >
                          {isSubmitting ? 'Salvando...' : 'Salvar'}
                        </button>
                      </Col>
                      <Col xs={12} sm={4} className="mb-2 mb-sm-0">
                        <button
                          type="button"
                          className="btn btn-primary btn-sm w-100"
                          onClick={() => resetForm({
                            values: {
                              corretorDesconto: 0,
                              corretorCarencia: 0,
                              gerenteDesconto: 0,
                              gerenteCarencia: 0,
                            }
                          })}
                          disabled={isSubmitting || removing}
                        >
                          Limpar Dados
                        </button>
                      </Col>
                      <Col xs={12} sm={4}>
                        <button
                          type="button"
                          className="btn btn-danger btn-sm w-100"
                          onClick={() => handleSubmitApagar()}
                        >
                          {removing ? 'Removendo...' : 'Remover Alçada'}
                        </button>
                      </Col>
                    </Row>
                  </Card.Body>
                </Card>
              </FormikForm>
            )}
          </Formik>
        </Col>

        {/* Coluna da Direita - Última Alteração */}
        <Col xs={12} lg={6}>
          <Card className="h-100">
            <Card.Header className="bg-light">
              <h5 className="mb-0">Últimas Alterações</h5>
            </Card.Header>
            <Card.Body className="p-0 p-sm-3">
              <style>
                {`
                  .tabela-alcadas tbody tr:hover {
                    background-color: #e8f4ff !important;
                  }
                  .tabela-alcadas thead th {
                    position: sticky;
                    top: 0;
                    z-index: 1;
                    background-color: #0747a6;
                  }
                  @media (max-width: 768px) {
                    .tabela-alcadas thead {
                      display: none;
                    }
                    .tabela-alcadas tbody tr {
                      display: block;
                      margin-bottom: 1rem;
                      border: 1px solid #dee2e6;
                      border-radius: 8px;
                      overflow: hidden;
                    }
                    .tabela-alcadas tbody td {
                      display: flex;
                      justify-content: space-between;
                      align-items: center;
                      padding: 0.75rem 1rem;
                      border: none;
                      border-bottom: 1px solid #eee;
                    }
                    .tabela-alcadas tbody td:last-child {
                      border-bottom: none;
                    }
                    .tabela-alcadas tbody td::before {
                      content: attr(data-label);
                      font-weight: 600;
                      color: #0747a6;
                      margin-right: 1rem;
                    }
                  }
                `}
              </style>
              <div style={tableStyles.wrapper}>
                <Table bordered hover size="sm" responsive className="mb-0 tabela-alcadas">
                  <thead>
                    <tr>
                      {colunasAlcadas.map(col => (
                        <th
                          key={col.chave}
                          style={{
                            ...tableStyles.header,
                            ...(col.sortable === false ? { cursor: 'default' } : {}),
                            ...(colunaHover === col.chave && col.sortable !== false
                              ? tableStyles.headerHover
                              : {}),
                          }}
                          onClick={() => col.sortable !== false && handleOrdenacao(col.chave)}
                          onMouseEnter={() => col.sortable !== false && setColunaHover(col.chave)}
                          onMouseLeave={() => setColunaHover(null)}
                        >
                          {col.label}
                          {col.sortable !== false && (
                            <span
                              style={{ fontSize: '0.75rem', marginLeft: '4px' }}
                            >
                              {getIconeOrdenacao(col.chave)}
                            </span>
                          )}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {dadosOrdenados && dadosOrdenados.length > 0 ? (
                      dadosOrdenados.map((item, index) => (
                        <tr key={index}>
                          <td data-label="Usuário">{item.nome_usuario}</td>
                          <td data-label="Setor">{item.setor}</td>
                          <td data-label="Tipo">{item.tipo}</td>
                          <td data-label="Alteração">
                            {item.tipo === 'Carência' ? (
                              <>De {item.de_valor} Para {item.paravalor}</>
                            ) : (
                              <>De {formatarPercentual(item.de_valor)} Para {formatarPercentual(item.paravalor)}</>
                            )}
                          </td>
                          <td data-label="Data">{formatarData(item.data_inclusao)}</td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan="5" className="text-center">Nenhum registro encontrado</td>
                      </tr>
                    )}
                  </tbody>
                </Table>
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </div>
  );
};

export default TelaAlcadas;
